import type { MfaLoginFactorType } from '@auth0/auth0-acul-react/types';
import BaseWrapper from 'creative-ui/oktaComponents/BaseWrapper';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MFA_REDIRECT_COOKIE } from '@/constants/mfa';
import { useTransactionLocale } from '@/hooks/useTranslationLocale';
import { deleteCookie, getCookie } from '@/utils/helpers/cookie';

import ChallengeMFASkeleton from './components/ChallengeMFASkeleton';
import MfaLoginOptionsList from './components/MfaLoginOptionsList';
import { useMfaLoginOptionsManager } from './hooks/useMfaLoginOptionsManager';

function MfaLoginOptionsScreen() {
  const { t } = useTranslation('mfaLoginOptions');
  useTransactionLocale();

  const {
    texts,
    handleReturnToPrevious,
    handleSelectFactor,
    supportedEnrolledFactors,
  } = useMfaLoginOptionsManager();

  // State for managing auto-redirect flow
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(true);

  // Check for redirect cookie on mount
  useEffect(() => {
    const checkRedirectCookie = async () => {
      const targetFactor = getCookie(MFA_REDIRECT_COOKIE);

      if (targetFactor && supportedEnrolledFactors.includes(targetFactor)) {
        // Cookie exists and factor is available - auto-redirect
        deleteCookie(MFA_REDIRECT_COOKIE);

        try {
          await handleSelectFactor(targetFactor as MfaLoginFactorType);
        } catch {
          // If redirect fails, show the options list
          setIsProcessingRedirect(false);
        }
      } else {
        // No cookie or target factor not available - show options
        if (targetFactor) {
          // Cookie existed but factor not available, clean it up
          deleteCookie(MFA_REDIRECT_COOKIE);
        }
        setIsProcessingRedirect(false);
      }
    };

    checkRedirectCookie();
  }, [handleSelectFactor, supportedEnrolledFactors.includes]);

  // SDK first, i18n fallback
  document.title = texts?.pageTitle || t('page.title');

  const title = texts?.title || t('header.title');
  const description = texts?.description || t('header.description');

  // Show skeleton mimicking target challenge screen while processing redirect
  if (isProcessingRedirect) {
    return <ChallengeMFASkeleton />;
  }

  return (
    <BaseWrapper backCtaProps={{ onClick: handleReturnToPrevious }}>
      <MfaLoginOptionsList title={title} description={description} />
    </BaseWrapper>
  );
}

export default MfaLoginOptionsScreen;
